/**
 * CustomerMessagingConsent — what this person has agreed to receive, read-only.
 *
 * Three rules the payload makes easy to get wrong, so they are structural here:
 *
 *  · **Preferences are channel × topic, not one switch per channel.** Three
 *    channels (email, SMS, WhatsApp) × three topics (bookings, events, general
 *    updates). Collapsing them would state one agreement where the person made
 *    up to nine.
 *  · **Unset is not consent, and it is not refusal.** WhatsApp is opt-in:
 *    missing or false does not permit sending. Email and SMS are opt-out: an
 *    unset preference is not an explicit no. "Not set" is rendered as the
 *    recorded state it is.
 *  · **A suppression overrides sending, and the stored preference stays
 *    visible underneath.** Suppressions come from the provider, sit ABOVE the
 *    grid, and never overwrite what the person chose — a blocked topic still
 *    shows the preference it is blocking.
 *
 * There are no mutation controls on this surface, by ruling. A failed read is
 * an error with a retry, never an endless spinner or an empty preference set.
 */
import { Btn, T } from "@v0y4ge/jrni-ui";
import { useT } from "../../lib/i18n";
import { friendlyDateShort } from "../../lib/datetime";
import { humaniseFieldKey } from "./customer-columns";
import {
  CHANNEL_OPT_IN, CONSENT_CHANNELS, CONSENT_TOPICS, getAdminCustomerMessagingPreferences, useScopedRead,
  type ConsentChannel, type ConsentTopic, type MessagingSuppression,
} from "./customers-data";
import { DetailSection, ReadError, useIcu } from "./customers-shared";

export default function CustomerMessagingConsent({ customerId }: { customerId: string }) {
  const t = useT();
  const icu = useIcu();
  const read = useScopedRead(`consent|${customerId}`, () => getAdminCustomerMessagingPreferences(customerId));
  const data = read.data?.data ?? null;

  const channelLabel = (c: string): string => t(`customers.consent.channel${c.charAt(0).toUpperCase()}${c.slice(1)}`, humaniseFieldKey(c));
  const topicLabel = (topic: ConsentTopic): string =>
    t(`customers.consent.topic${topic.charAt(0).toUpperCase()}${topic.slice(1)}`, humaniseFieldKey(topic));

  /** A suppression's channel may be ANY delivery-channel value, not just the
   *  three with a preference grid — labelled without assuming the short list. */
  const suppressionChannelLabel = (s: MessagingSuppression) => channelLabel(String(s.channel ?? ""));

  const suppressedFor = (channel: ConsentChannel, topic: ConsentTopic): MessagingSuppression | null =>
    (data?.suppressions ?? []).find((s) => s.channel === channel && (!s.topic || s.topic === topic)) ?? null;

  return (
    <DetailSection title={t("customers.consent.title", "Messaging consent")}>
      {read.failure ? (
        <ReadError
          message={read.failure.denied
            ? t("customers.consent.unavailable", "Messaging consent isn\u2019t available to you.")
            : `${t("customers.consent.loadFailed", "Messaging consent couldn\u2019t be read.")} ${read.failure.message}`}
          onRetry={read.failure.denied ? undefined : read.reload}
          retryLabel={t("common.actions.retry", "Retry")}
        />
      ) : read.loading ? (
        <p style={{ margin: 0, padding: "14px 16px", fontSize: 12.5, color: T.soft }}>{t("customers.consent.loading", "Loading consent\u2026")}</p>
      ) : !data ? (
        <p style={{ margin: 0, padding: "14px 16px", fontSize: 12.5, color: T.soft }}>{t("customers.consent.empty", "No messaging consent recorded.")}</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "14px 16px" }}>
          <p style={{ margin: 0, fontSize: 12, color: T.soft, lineHeight: 1.5 }}>
            {t("customers.consent.introRead", "What this person has agreed to receive. Suppressions come from the provider and override consent.")}
          </p>

          {/* Suppressions sit ABOVE the grid they override. */}
          {data.suppressions.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: T.n1 }}>{t("customers.consent.suppressionsTitle", "Provider suppressions")}</span>
              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                {data.suppressions.map((s, i) => (
                  <li key={`${s.channel}-${s.topic ?? "all"}-${i}`} style={{ display: "flex", flexDirection: "column", gap: 2, padding: "8px 10px", borderRadius: 6, background: T.orange2 }}>
                    <span style={{ fontSize: 12.5, color: T.n1 }}>
                      {s.topic
                        ? icu("customers.consent.suppression.rowTopic", "{channel} suppressed for {topic} since {date}: {reason}", {
                          channel: suppressionChannelLabel(s),
                          topic: topicLabel(s.topic as ConsentTopic),
                          date: s.createdAt ? friendlyDateShort(s.createdAt) : t("customers.consent.dateUnknown", "an unrecorded date"),
                          reason: s.reason ?? t("customers.consent.reasonUnknown", "no reason recorded"),
                        })
                        : icu("customers.consent.suppression.rowChannel", "{channel} suppressed since {date}: {reason}", {
                          channel: suppressionChannelLabel(s),
                          date: s.createdAt ? friendlyDateShort(s.createdAt) : t("customers.consent.dateUnknown", "an unrecorded date"),
                          reason: s.reason ?? t("customers.consent.reasonUnknown", "no reason recorded"),
                        })}
                    </span>
                    {s.source || s.address ? (
                      <span style={{ fontSize: 11.5, color: T.n2 }}>
                        {[s.source ? icu("customers.consent.suppression.source", "Source: {source}", { source: s.source }) : null, s.address]
                          .filter(Boolean).join(" \u00b7 ")}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
              <span style={{ fontSize: 11.5, color: T.soft }}>{t("customers.consent.suppression.outranks", "A suppression overrides the preference below it.")}</span>
            </div>
          ) : null}

          {/* channel × topic grid */}
          <div style={{ display: "grid", gridTemplateColumns: `minmax(96px, 1fr) repeat(${CONSENT_TOPICS.length}, minmax(84px, 1fr))`, gap: "8px 10px", minWidth: 0 }}>
            <span />
            {CONSENT_TOPICS.map((topic) => (
              <span key={topic} style={{ fontSize: 11, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: "0.04em" }}>{topicLabel(topic)}</span>
            ))}
            {CONSENT_CHANNELS.map((channel) => (
              <ChannelRow
                key={channel}
                channel={channel}
                label={channelLabel(channel)}
                prefs={data.preferences[channel]}
                suppressedFor={suppressedFor}
              />
            ))}
          </div>
        </div>
      )}
    </DetailSection>
  );
}

function ChannelRow({ channel, label, prefs, suppressedFor }: {
  channel: ConsentChannel;
  label: string;
  prefs: Partial<Record<ConsentTopic, boolean>> | undefined;
  suppressedFor: (channel: ConsentChannel, topic: ConsentTopic) => MessagingSuppression | null;
}) {
  const t = useT();
  const optIn = CHANNEL_OPT_IN[channel];
  return (
    <>
      <span style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: T.n1 }}>{label}</span>
        <span style={{ fontSize: 10.5, color: T.soft }}>
          {optIn ? t("customers.consent.optInHint", "Only sends after an explicit opt-in.") : t("customers.consent.optOutHint", "Sends unless this person opts out.")}
        </span>
      </span>
      {CONSENT_TOPICS.map((topic) => {
        const value = prefs?.[topic];
        const suppression = suppressedFor(channel, topic);
        const state = value === undefined
          ? t("customers.consent.topicUnset", "Not set")
          : value
            ? t("customers.consent.topicAllowed", "Allowed")
            : t("customers.consent.topicBlocked", "Not allowed");
        const tone = value === undefined ? T.soft : value ? T.green1 : T.n2;
        return (
          <span key={topic} style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: tone }}>{state}</span>
            {suppression ? (
              <span style={{ fontSize: 10.5, color: T.orange1 }}>{t("customers.consent.suppressed", "Blocked by a suppression")}</span>
            ) : null}
          </span>
        );
      })}
    </>
  );
}
